// src/components/settings/VetSettings.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Table, TableRow } from '@mui/material';
import { referenceDataAPI, Vet, VetReferenceUsage } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import { ConfirmDeleteDialog, RefCountChip } from './shared/SettingsElements';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import SettingsTableActions from './shared/SettingsTableActions';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableCellWithIcon,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import VetDialog from './shared/dialogs/VetDialog';
import LoadingSpinner from '../common/LoadingSpinner';

const EMPTY_USAGE: VetReferenceUsage = { vets: {} };

const VetsSettings: React.FC = () => {
  const [vets, setVets] = useState<Vet[]>([]);
  const [usage, setUsage] = useState<VetReferenceUsage>(EMPTY_USAGE);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVet, setEditingVet] = useState<Vet | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const { handleError } = useErrorHandler();
  const { showSuccess } = useGlobalToast();

  const fetchUsage = useCallback(async () => {
    try {
      setUsage(await referenceDataAPI.getVetReferenceUsage());
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const fetchVets = useCallback(async () => {
    try {
      setLoading(true);
      const [data] = await Promise.all([
        referenceDataAPI.getVets(),
        fetchUsage(),
      ]);
      setVets(data);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError, fetchUsage]);

  useEffect(() => {
    fetchVets();
  }, [fetchVets]);

  const filteredVets = useMemo(() => {
    if (!searchText) return vets;
    const lower = searchText.toLowerCase();
    return vets.filter(
      (v) =>
        v.name.toLowerCase().includes(lower) ||
        v.phone?.toLowerCase().includes(lower) ||
        v.email?.toLowerCase().includes(lower)
    );
  }, [vets, searchText]);

  const handleOpenDialog = (vet?: Vet) => {
    setEditingVet(vet ?? null);
    setDialogOpen(true);
  };

  const handleSave = async (formData: {
    name: string;
    phone: string;
    email: string;
  }) => {
    try {
      if (editingVet) {
        await referenceDataAPI.updateVet(editingVet.id, formData);
        showSuccess();
      } else {
        await referenceDataAPI.createVet(formData);
        showSuccess();
      }
      await fetchVets();
      setDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await referenceDataAPI.deleteVet(deleteTarget.id);
      showSuccess();
      await fetchVets();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading Vets..." size={80} />;
  }

  return (
    <Box>
      <SettingsHeaderRow
        title="Manage Vets"
        onAdd={() => handleOpenDialog()}
        addLabel="Add Vet"
        onSearch={setSearchText}
        onClearSearch={() => setSearchText('')}
        searchText={searchText}
        searchPlaceholder="Search vets..."
        buttonMinWidth="192px"
      />

      {vets.length === 0 ? (
        <Alert severity="info">
          No vets found. Click "Add Vet" to create one.
        </Alert>
      ) : filteredVets.length === 0 ? (
        <Alert severity="info">No vets match "{searchText}".</Alert>
      ) : (
        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                <StyledTableHeaderCell>Phone</StyledTableHeaderCell>
                <StyledTableHeaderCell>Email</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Refs
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {filteredVets.map((vet, index) => {
                const refCount = usage.vets[vet.id] ?? 0;
                return (
                  <StyledTableRow key={vet.id} isOdd={index % 2 === 0}>
                    <StyledTableCell>
                      <Typography>{vet.name}</Typography>
                    </StyledTableCell>
                    <StyledTableCellWithIcon>
                      {vet.phone && (
                        <Box>
                          <Typography>{vet.phone}</Typography>
                        </Box>
                      )}
                    </StyledTableCellWithIcon>
                    <StyledTableCell>
                      {vet.email && (
                        <Box display="flex" alignItems="center" gap={1}>
                          {vet.email}
                        </Box>
                      )}
                    </StyledTableCell>
                    <StyledTableChipCell>
                      <StyledTableChipCellContent>
                        <RefCountChip count={refCount} />
                      </StyledTableChipCellContent>
                    </StyledTableChipCell>
                    <StyledTableActionsCell align="right">
                      <SettingsTableActions
                        itemId={vet.id}
                        itemName={vet.name}
                        refCount={refCount}
                        onEdit={() => handleOpenDialog(vet)}
                        onDelete={() =>
                          setDeleteTarget({ id: vet.id, name: vet.name })
                        }
                        refLabel="client"
                      />
                    </StyledTableActionsCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <VetDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        editing={editingVet}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete vet?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default VetsSettings;
